import { useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import useSectionReveal from '@/hooks/useSectionReveal';
import { processSteps, processImage } from '@/mocks/sections';

gsap.registerPlugin(ScrollTrigger, useGSAP);

export default function ProcessSection() {
  const ref = useRef<HTMLElement>(null);
  const [active, setActive] = useState(0);
  const [animated, setAnimated] = useState(false);
  useSectionReveal(ref);

  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: no-preference)', () => {
      setAnimated(true);
      gsap.fromTo('.ps-fill', { scaleY: 0 }, {
        scaleY: 1, ease: 'none',
        scrollTrigger: { trigger: '.ps-list', start: 'top 60%', end: 'bottom 60%', scrub: 0.8 },
      });
      gsap.utils.toArray<HTMLElement>('.ps-step', ref.current).forEach((el, i) => {
        ScrollTrigger.create({
          trigger: el, start: 'top 60%', end: 'bottom 60%',
          onEnter: () => setActive(i), onEnterBack: () => setActive(i),
        });
      });
      return () => setAnimated(false);
    });
    mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => {
      gsap.fromTo('.ps-img', { y: -16 }, {
        y: 16, ease: 'none',
        scrollTrigger: { trigger: ref.current, start: 'top bottom', end: 'bottom top', scrub: 0.9 },
      });
    });
    return () => mm.revert();
  }, { scope: ref });

  return (
    <section ref={ref} id="process" className="bg-background-50 px-4 md:px-10 py-20 md:py-32">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-[12vh]">
          <p data-reveal className="text-[11px] uppercase tracking-[0.24em] text-primary-600">Installation process</p>
          <h2 data-reveal className="mt-4 font-heading font-light text-3xl md:text-6xl leading-[1.1] text-foreground-950">Four calm steps.</h2>
          <div data-reveal-img className="mt-10 w-full h-[380px] md:h-[520px] lg:h-[min(520px,62vh)] overflow-hidden">
            <img loading="lazy" decoding="async" src={processImage} alt="Installer sealing a window frame" title="Clean window installation" className="ps-img w-full h-[calc(100%+32px)] -mt-4 object-cover object-top will-change-transform" />
          </div>
        </div>
        <ol className="ps-list relative lg:col-span-6 lg:col-start-7 lg:pt-32">
          <span aria-hidden className="absolute left-[5px] top-0 lg:top-32 bottom-0 w-px bg-background-300" />
          <span aria-hidden className="ps-fill absolute left-[4.5px] top-0 lg:top-32 bottom-0 w-[2px] origin-top bg-primary-700" style={{ transform: animated ? undefined : 'scaleY(1)' }} />
          {processSteps.map((s, i) => {
            const on = !animated || i <= active;
            const current = !animated || i === active;
            return (
              <li key={s.n} className="ps-step relative pl-8 md:pl-10 grid grid-cols-[48px_1fr] md:grid-cols-[88px_1fr] gap-4 py-8 md:py-10 lg:py-14">
                <span aria-hidden className={`absolute left-0 top-[2.6rem] md:top-[3.3rem] lg:top-[4.3rem] w-[11px] h-[11px] rounded-full border transition-all duration-500 ${current ? 'bg-primary-700 border-primary-700 scale-110' : on ? 'bg-primary-700/60 border-primary-700/60' : 'bg-background-50 border-background-400'}`} />
                <span className="absolute left-8 md:left-10 right-0 bottom-0 h-px bg-background-300/70" />
                <span className={`absolute left-8 md:left-10 right-0 bottom-0 h-px origin-left bg-primary-700/70 transition-transform duration-[600ms] ease-out ${on ? 'scale-x-100' : 'scale-x-0'}`} />
                <span className={`font-heading text-2xl md:text-4xl font-light transition-colors duration-500 ${current ? 'text-primary-800' : 'text-accent-600/70'}`}>{s.n}</span>
                <div className={`transition-all duration-[550ms] ease-out ${current ? 'opacity-100 translate-y-0' : 'opacity-60 translate-y-[12px]'}`}>
                  <h3 className="font-heading text-xl md:text-2xl text-primary-900">{s.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{s.text}</p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}